import { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, X, Plug, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { TOUR_STEPS } from "@/hooks/useGuidedTour";

interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
}

interface GuidedTourProps {
  active: boolean;
  currentStep: number;
  totalSteps: number;
  onNext: () => void;
  onSkip: () => void;
}

export function GuidedTour({ active, currentStep, totalSteps, onNext, onSkip }: GuidedTourProps) {
  const [targetRect, setTargetRect] = useState<Rect | null>(null);
  const rafRef = useRef<number>(0);
  const step = TOUR_STEPS[currentStep];

  const updateRect = useCallback(() => {
    if (!step) return;
    const el = document.querySelector(`[data-tour="${step.target}"]`);
    if (el) {
      const r = el.getBoundingClientRect();
      setTargetRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    } else {
      setTargetRect(null);
    }
  }, [step]);

  // Recalculate on step change and on scroll/resize
  useEffect(() => {
    if (!active) return;
    updateRect();
    const handle = () => {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(updateRect);
    };
    window.addEventListener("resize", handle);
    window.addEventListener("scroll", handle, true);
    return () => {
      window.removeEventListener("resize", handle);
      window.removeEventListener("scroll", handle, true);
      cancelAnimationFrame(rafRef.current);
    };
  }, [active, updateRect]);

  if (!active || !step) return null;

  const pad = 6;
  const spotlightStyle = targetRect
    ? {
        clipPath: `polygon(
          0% 0%, 0% 100%, 
          ${targetRect.left - pad}px 100%, 
          ${targetRect.left - pad}px ${targetRect.top - pad}px, 
          ${targetRect.left + targetRect.width + pad}px ${targetRect.top - pad}px, 
          ${targetRect.left + targetRect.width + pad}px ${targetRect.top + targetRect.height + pad}px, 
          ${targetRect.left - pad}px ${targetRect.top + targetRect.height + pad}px, 
          ${targetRect.left - pad}px 100%, 
          100% 100%, 100% 0%
        )`,
      }
    : {};

  // Tooltip position: right of target, centered vertically
  const tooltipTop = targetRect ? targetRect.top + targetRect.height / 2 : "50%";
  const tooltipLeft = targetRect ? targetRect.left + targetRect.width + pad + 16 : "50%";

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[90]" onClick={(e) => e.stopPropagation()}>
        {/* Dark overlay with spotlight hole */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="absolute inset-0 bg-black/70"
          style={spotlightStyle}
        />

        {/* Spotlight glow ring */}
        {targetRect && (
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
            className="absolute rounded-lg border-2 border-primary/60 pointer-events-none"
            style={{
              top: targetRect.top - pad,
              left: targetRect.left - pad,
              width: targetRect.width + pad * 2,
              height: targetRect.height + pad * 2,
              boxShadow: "0 0 0 4px hsl(var(--primary) / 0.2), 0 0 20px 4px hsl(var(--primary) / 0.15)",
            }}
          />
        )}

        {/* Tooltip */}
        <motion.div
          key={`tooltip-${currentStep}`}
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -10 }}
          transition={{ duration: 0.3, delay: 0.15 }}
          className="absolute w-72 sm:w-80"
          style={{
            top: typeof tooltipTop === "number" ? tooltipTop - 60 : tooltipTop,
            left: typeof tooltipLeft === "number" ? Math.min(tooltipLeft, window.innerWidth - 340) : tooltipLeft,
          }}
        >
          <div className="bg-card border border-border rounded-xl shadow-2xl p-5 relative">
            {/* Skip */}
            <button
              onClick={onSkip}
              className="absolute top-3 right-3 text-muted-foreground hover:text-foreground transition-colors"
            >
              <X className="h-4 w-4" />
            </button>

            {/* Step indicator */}
            <div className="flex items-center gap-1.5 mb-3">
              {Array.from({ length: totalSteps }).map((_, i) => (
                <div
                  key={i}
                  className={`h-1.5 rounded-full transition-all duration-300 ${
                    i === currentStep
                      ? "w-6 bg-primary"
                      : i < currentStep
                        ? "w-3 bg-primary/40"
                        : "w-3 bg-muted-foreground/20"
                  }`}
                />
              ))}
            </div>

            <div className="flex items-start gap-3">
              <span className="text-2xl flex-shrink-0">{step.icon}</span>
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-semibold text-foreground mb-1">{step.title}</h3>
                <p className="text-xs text-muted-foreground leading-relaxed">{step.description}</p>
              </div>
            </div>

            <div className="flex items-center justify-between mt-4">
              <span className="text-[10px] text-muted-foreground">
                {currentStep + 1} de {totalSteps}
              </span>
              <Button size="sm" onClick={onNext} className="text-xs px-4">
                {currentStep < totalSteps - 1 ? (
                  <>Siguiente <ArrowRight className="h-3.5 w-3.5 ml-1" /></>
                ) : (
                  "¡Listo!"
                )}
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}

/* Completion modal — what to do next */
interface TourCompletionProps {
  visible: boolean;
  onDismiss: () => void;
}

export function TourCompletion({ visible, onDismiss }: TourCompletionProps) {
  const navigate = useNavigate();

  if (!visible) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          transition={{ type: "spring", stiffness: 300, damping: 25 }}
          className="bg-card border border-border rounded-2xl shadow-2xl p-6 sm:p-8 max-w-md mx-4"
        >
          <div className="text-center mb-6">
            <motion.span
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", delay: 0.2 }}
              className="text-5xl block mb-4"
            >
              🎉
            </motion.span>
            <h2 className="text-xl font-display font-bold text-foreground mb-2">
              ¡Tour completado!
            </h2>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Ya conoces los módulos principales. Ahora elige por dónde empezar:
            </p>
          </div>

          <div className="grid gap-3">
            <button
              onClick={() => { onDismiss(); navigate("/app/onboarding"); }}
              className="flex items-center gap-3 p-4 rounded-xl border border-border bg-background hover:bg-accent/50 transition-colors text-left group"
            >
              <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0 group-hover:bg-primary/20 transition-colors">
                <Plug className="h-5 w-5 text-primary" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-foreground">Conectar un canal</p>
                <p className="text-xs text-muted-foreground">WhatsApp, Instagram, Messenger…</p>
              </div>
              <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors" />
            </button>

            <button
              onClick={() => { onDismiss(); navigate("/app/onboarding-ai-agent"); }}
              className="flex items-center gap-3 p-4 rounded-xl border border-border bg-background hover:bg-accent/50 transition-colors text-left group"
            >
              <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0 group-hover:bg-primary/20 transition-colors">
                <Sparkles className="h-5 w-5 text-primary" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-foreground">Crear un asistente IA</p>
                <p className="text-xs text-muted-foreground">Responde automáticamente 24/7</p>
              </div>
              <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors" />
            </button>
          </div>

          <button
            onClick={onDismiss}
            className="w-full mt-4 text-xs text-muted-foreground hover:text-foreground transition-colors text-center py-2"
          >
            Explorar por mi cuenta
          </button>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
